<template>
    <main class="order-and-pay-view-component">
        <section class="order-and-pay-sections-container">
            <section class="bill-section" v-if="isInitialized && isAllocated">
                <header>
                    <PaymentServicesList class="scrollable-horizontal" />
                    <div class="table-allocation-info-container">
                        <OperatorTableAllocationInfoButton />
                    </div>
                </header>
                <section class="bill-section-content">
                    <BillSummarySection
                        v-if="!bill.isLoading && !cartPayment.isProcessingPaymentDevicePayment"
                        ref="billSummarySectionComponent"
                    />

                    <div
                        class="bill-section-placeholder"
                        v-if="bill.isLoading && !cartPayment.isProcessingPaymentDevicePayment"
                    >
                        {{ translations.billSection.loadingData }}
                    </div>
                    <div
                        class="bill-section-placeholder"
                        v-if="!bill.isLoading && cartPayment.isProcessingPaymentDevicePayment"
                    >
                        {{ translations.billSection.processingPaymentDevicePayment }}
                    </div>
                </section>
                <footer class="order-actions-footer" v-if="allocation.hasPaymentServices">
                    <SelectedItemsSumLabel :count="bill.totalItemsSelected" />
                    <div class="action-buttons-container">
                        <ProceedButton
                            class="pay-bill-button"
                            :currentPrice="bill.totalPayableAfterDiscount"
                            :disabled="!isPayButtonActive"
                            @click="payForBill"
                        >
                            <span v-if="!cartPayment.isProcessingPayment">{{ translations.general.continue }}</span>
                            <span v-else>{{ translations.general.addingButtonPlaceholder }}</span>
                        </ProceedButton>
                    </div>
                </footer>

                <PaymentConfirmationPopover
                    v-if="transactionIdInProgress != null || externalPaymentIdInProgress != null"
                    :transactionFor="transactionForInProgress ?? PaymentFor.Order"
                    :transactionId="transactionIdInProgress"
                    :externalPaymentId="externalPaymentIdInProgress"
                    @closed="onClosePaymentConfirmationPopover"
                />

                <PayPopover
                    v-if="isPayPopoverVisible"
                    :billId="bill.currentBill?.id ?? ''"
                    :consumption="payPopoverOptions!.consumption"
                    :selectedItems="payPopoverOptions!.selectedItems"
                    :discountAmount="bill.totalDiscountsAndVouchersAmount"
                    :fees="0"
                    :hasDiscounts="bill.hasDiscounts"
                    :totalPayableAfterDiscount="bill.totalPayableAfterDiscount"
                    @closed="closePaymentPopover"
                    @paymentSuccess="onPaymentSuccess"
                />
            </section>
            <section class="offer-section" v-if="isInitialized && isAllocated">
                <header :class="{ 'has-discount-button': areDiscountsEnabled }">
                    <OrderServiceList class="scrollable-horizontal" />
                    <div class="discount-button-container" v-if="areDiscountsEnabled">
                        <DiscountButton @onClick="discountSelectedCartItems" />
                    </div>
                </header>

                <div
                    class="back-to-preoder-time-btn-container"
                    v-if="showGoBackToPreorderTimeSelectionButton"
                    @click="goBackToPreorderTimeSelection"
                >
                    <div>
                        <i class="fal fa-arrow-left"></i>
                    </div>
                </div>

                <template v-if="!showPreorderTimeSelection">
                    <section class="offer-view">
                        <ScrollableView class="item-categories" v-if="!offer.isLoading && offer.hasItemCategories">
                            <OfferItemCategoriesList
                                :showOfferCategoryImages="showOfferCategoryImages"
                                @categorySelected="onItemsCategorySelected"
                            />
                        </ScrollableView>
                        <ScrollableView
                            ref="itemsContainer"
                            class="offer-items-section"
                            v-if="!offer.isLoading && offer.hasItemCategories"
                            :class="{ 'is-preorder-offer': showGoBackToPreorderTimeSelectionButton }"
                        >
                            <section class="items-container">
                                <OfferItemTile
                                    v-for="item in offer.offerItems"
                                    :key="item.articleId"
                                    :item="item"
                                    :isAvailable="isItemAvailable(item)"
                                    @addToCartPopoverClick="selectItem(item)"
                                    @quickAddClick="quickAddItem(item)"
                                />
                            </section>
                        </ScrollableView>
                        <div class="no-active-offer-msg" v-if="!offer.isLoading && !offer.hasItemCategories">
                            <span>{{ translations.offerSection.noActiveOffer }}</span>
                        </div>
                        <div class="offer-loader-container" v-if="offer.isLoading">
                            <span>{{ translations.offerSection.loadingData }}</span>
                        </div>
                    </section>
                    <footer class="order-actions-footer" v-if="offer.hasItemCategories">
                        <SelectedItemsSumLabel :count="cart.totalCartMainItems" />
                        <OrderServiceActionButtons />
                    </footer>
                </template>

                <template v-if="showPreorderTimeSelection">
                    <section class="preorder-time-view">
                        <PreorderTimeComponent
                            :initializeOnMounted="true"
                            v-model:selectedOrderDateAndTime="selectedOrderDateAndTime"
                            :selectedServiceId="selectedOrderServiceId"
                            :showHeader="false"
                            @orderDateAndTimeSelected="onOrderDateAndTimeSelected"
                            ref="preorderTimeComponent"
                        />
                    </section>
                    <footer class="preorder-actions-footer order-actions-footer">
                        <div class="action-buttons-container">
                            <ProceedButton
                                class="select-preorder-time-button"
                                :currentPrice="0"
                                :disabled="!isOrderDateAndTimeSelected"
                                @click="onSelectPreorderTimeButtonClick"
                            >
                                <span>
                                    {{ translations.general.continue }}
                                </span>
                            </ProceedButton>
                        </div>
                    </footer>
                </template>

                <div v-if="selectedItem" class="add-to-cart-popover-container">
                    <div class="blank-column"></div>
                    <AddToCartPopover
                        ref="addToCartPopover"
                        v-if="selectedItem"
                        :item="selectedItem"
                        @closed="unselectItem"
                        @itemAdded="onCartItemAdded"
                    />
                </div>

                <ApplicableDiscountsModal ref="applicableDiscountsModalComponent" :applyToCart="true" />
            </section>
            <div class="loader-container" v-if="!isInitialized">
                <div>
                    <span>{{ translations.offerSection.loadingData }}</span>
                    <div class="loader"></div>
                </div>
            </div>
            <div class="loading-error-container" v-if="isInitialized && !isAllocated">
                <div>
                    <span>{{ trans_vue.orderAndPayView.pleaseSelectAnotherTable }}</span>
                </div>
            </div>
        </section>
        <footer v-if="isInitialized">
            <span v-if="isAllocated" class="footer-location-name">{{ locationName }}</span>
            <span class="go-to-room-plan-btn" @click="goToRoomPlan">
                <i class="fal fa-arrow-right-from-bracket"></i>
            </span>
        </footer>
    </main>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { getPreselectedOptions } from "@/components/order/preselectedArticleOptions";
    import { useAddToCart } from "@/components/order/useAddToCart";
    import type { ICustomerArticleWithOptions, SelectedOrderDateAndTime } from "@/data/model/Customer";
    import { PaymentFor } from "@/data/model/Enums";
    import { CurrentLocationService } from "@/data/services/CurrentLocationService";
    import { useBillStore } from "@/data/stores/bill";
    import { useCartStore } from "@/data/stores/cart";
    import { useCartPaymentStore } from "@/data/stores/cartPayment";
    import { useIdentityStore } from "@/data/stores/identity";
    import { useOfferStore } from "@/data/stores/offer";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { OperatorClient } from "@/operator/OperatorClient";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { trans_vue } from "@/operator/localization/TransVue";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from "vue";

    import SelectedItemsSumLabel from "@/components/cart/SelectedItemsSumLabel.vue";
    import ProceedButton from "@/components/elements/ProceedButton.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import OfferItemCategoriesList from "@/components/offer/OfferItemCategoriesList.vue";
    import OfferItemTile from "@/components/offer/OfferItemTile.vue";
    import PreorderTimeComponent from "@/components/preorder/PreorderTimeComponent.vue";
    import ApplicableDiscountsModal from "@/operator/components/allocation/ApplicableDiscountsModal.vue";
    import OperatorTableAllocationInfoButton from "@/operator/components/allocation/OperatorTableAllocationInfoButton.vue";
    import BillSummarySection from "@/operator/components/bill/BillSummarySection.vue";
    import AddToCartPopover from "@/operator/components/cart/AddToCartPopover.vue";
    import DiscountButton from "@/operator/components/offer/DiscountButton.vue";
    import OrderServiceList from "@/operator/components/offer/OrderServiceList.vue";
    import OrderServiceActionButtons from "@/operator/components/order/OrderServiceActionButtons.vue";
    import PayPopover from "@/operator/components/payment/PayPopover.vue";
    import PaymentConfirmationPopover from "@/operator/components/payment/PaymentConfirmationPopover.vue";
    import PaymentServicesList from "@/operator/components/payment/PaymentServicesList.vue";

    import { useOrderAndPayDataContext } from "./OrderAndPayViewDataContext";

    // stores
    const bill = useBillStore();
    const cart = useCartStore();
    const offer = useOfferStore();
    const allocation = useTableAllocationStore();
    const identity = useIdentityStore();
    const cartPayment = useCartPaymentStore();

    // define properties
    const props = defineProps<{
        tableId: string;
        roomId?: string;
        billId?: string;
        transactionId?: string;
        externalPaymentId?: string;
        transactionFor?: PaymentFor;
    }>();

    const {
        // refs
        showPreorderTimeSelection,
        isInitialized,
        selectedItem,
        isPayPopoverVisible,
        payPopoverOptions,
        externalPaymentIdInProgress,
        transactionIdInProgress,
        transactionForInProgress,

        // getters
        isAllocated,
        isPayButtonActive,
        locationName,

        // actions
        payForBill,
        closePaymentPopover,
        onPaymentSuccess,
        isItemAvailable,
        selectItem,
        unselectItem,
        goToRoomPlan,
        initView,
        disposeView,
    } = useOrderAndPayDataContext(
        CurrentLocationService.getLocationId()!,
        props.tableId,
        props.billId,
        props.transactionId,
        props.externalPaymentId,
        props.transactionFor
    );

    // define html refs
    const billSummarySectionComponent = ref<InstanceType<typeof BillSummarySection> | null>(null);
    function onCartItemAdded() {
        billSummarySectionComponent.value?.focusOnCartSection();
    }

    const itemsContainer = ref<InstanceType<typeof ScrollableView> | null>(null);
    function onItemsCategorySelected() {
        itemsContainer.value?.scrollToTop();
    }

    // drives both the button itself and the room reserved for it in the header
    const areDiscountsEnabled = computed(() => offer.areDiscountsEnabled);

    // the offer category images follow the order application's switch for operators, like the item images
    // (OfferItemTile): off means no images for operators and admins anywhere
    const showOfferCategoryImages = computed(() => offer.orderService?.useCase?.operatorDisplayItemImages === true);

    const applicableDiscountsModalComponent = ref<InstanceType<typeof ApplicableDiscountsModal> | null>(null);
    function discountSelectedCartItems() {
        applicableDiscountsModalComponent.value?.show();
    }

    onBeforeMount(() => {
        identity.setIdentity(OperatorClient.context.profile!);
    });

    // define on mounted actions
    onMounted(initView);
    onBeforeUnmount(disposeView);

    //#region Preorder
    const preorderTimeComponent = ref<InstanceType<typeof PreorderTimeComponent> | null>(null);
    const selectedOrderServiceId = computed(() => offer.orderService?.id);
    const selectedOrderDateAndTime = ref<SelectedOrderDateAndTime | null>(null);
    const addToCartPopover = ref<InstanceType<typeof AddToCartPopover> | null>(null);
    const isOrderDateAndTimeSelected = computed(
        () => selectedOrderDateAndTime.value?.date != null && selectedOrderDateAndTime.value?.time != null
    );
    const showGoBackToPreorderTimeSelectionButton = computed(
        () => offer.isPreorderService && !showPreorderTimeSelection.value
    );

    function onSelectPreorderTimeButtonClick() {
        preorderTimeComponent.value?.selectOrderDateAndTime();
    }

    async function onOrderDateAndTimeSelected() {
        await offer.loadOffer();
        showPreorderTimeSelection.value = false;
    }

    function goBackToPreorderTimeSelection() {
        showPreorderTimeSelection.value = true;
    }

    function onGoBackToPreorderTimeSelectionRequested() {
        goBackToPreorderTimeSelection();
    }

    function onClosePaymentConfirmationPopover() {
        transactionIdInProgress.value = undefined;
        transactionForInProgress.value = undefined;
        externalPaymentIdInProgress.value = undefined;
    }

    onMounted(() => {
        operatorEvents.payContext.goBackToPreorderTimeSelectionRequested.add(onGoBackToPreorderTimeSelectionRequested);
    });

    onBeforeUnmount(() => {
        operatorEvents.payContext.goBackToPreorderTimeSelectionRequested.remove(
            onGoBackToPreorderTimeSelectionRequested
        );
    });

    watch(
        () => offer.orderService,
        async () => {
            console.log("orderService changed", offer.orderService);
            console.log("isPreorderService", offer.isPreorderService);

            if (addToCartPopover.value) {
                console.log("Close add to cart popover");
                unselectItem();
            }

            if (!offer.isPreorderService) {
                showPreorderTimeSelection.value = false;
                return;
            }

            // Hide preorder state to reset the view
            showPreorderTimeSelection.value = false;
            selectedOrderDateAndTime.value = null;

            await cart.loadCurrentCart();
            const isEmptyCart = cart.currentCart == null;
            console.log("isEmptyCart", isEmptyCart);
            if (isEmptyCart) {
                showPreorderTimeSelection.value = true;
                return;
            }

            const isCartForDifferentService = cart.currentCart?.serviceId != offer.orderService?.id;
            console.log("isCartForDifferentService", isCartForDifferentService);
            if (isCartForDifferentService) {
                showPreorderTimeSelection.value = true;
                return;
            }

            const hasNoPreorderTimeSelected = cart.currentCart?.preorderDate == null;
            console.log("hasNoPreorderTimeSelected", hasNoPreorderTimeSelected);
            if (hasNoPreorderTimeSelected) {
                showPreorderTimeSelection.value = true;
                return;
            }

            showPreorderTimeSelection.value = false;
        }
    );
    //#endregion

    // one tap on a tile reaches this twice: the tap recognizer fires on touchend and the
    // synthetic click that follows it slips past the 350ms suppression on a slow device.
    // keyed by article so a tap on another tile still goes through while this one runs
    const quickAddInProgress = new Set<string>();

    async function quickAddItem(item: ICustomerArticleWithOptions) {
        if (quickAddInProgress.has(item.articleId)) return;
        quickAddInProgress.add(item.articleId);

        try {
            const { selectedOptions, isAddToCartAvailable, addItemToCart, fixCurrentQuantity } = useAddToCart(
                item,
                offer.orderService,
                true
            );
            fixCurrentQuantity();
            selectedOptions.value = getPreselectedOptions(item);

            if (isAddToCartAvailable.value) {
                await addItemToCart();
                onCartItemAdded();
            } else {
                throw new Error("Add to cart is not available");
            }
        } catch (ex) {
            logError(ex);
            Popup.show({ message: trans_vue.cart.failedToAdd });
        } finally {
            quickAddInProgress.delete(item.articleId);
        }
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .order-and-pay-view {
        display: flex;

        .order-and-pay-component {
            flex: 1 1 auto;
            display: flex;
        }

        .order-and-pay-view-component {
            flex: 1 1 auto;
        }
    }

    .order-and-pay-view-component {
        flex: 1 1 auto;

        display: flex;
        flex-direction: column;

        > .order-and-pay-sections-container {
            flex: 1 1 auto;
            margin: 0 auto;
            width: 100%;
            max-width: 1300px;

            display: flex;

            > section {
                &.bill-section {
                    flex: 1 1 40%;
                    border-left: 1px solid colors.$lightGrey;
                    border-right: 1px solid colors.$lightGrey;
                    display: flex;
                    flex-direction: column;
                    position: relative;

                    > header {
                        background-color: colors.$lightGreyBackground;
                        border-bottom: 1px solid colors.$greyBorder;
                        padding: 0.5em 1em;
                        flex: 0 0 auto;
                        position: relative;
                        height: 62px;

                        .table-allocation-info-container {
                            position: absolute;
                            right: 0;
                            top: 0;
                            bottom: 0;
                            padding: 13px;
                        }
                    }

                    > .bill-section-content {
                        flex: 1 1 auto;
                        display: flex;

                        > .bill-summary-section {
                            flex: 1 1 auto;
                        }

                        > .bill-section-placeholder {
                            padding: 2em;
                            flex: 1 1 auto;
                            text-align: center;
                        }
                    }

                    > footer {
                        flex: 0 0 auto;
                    }
                }

                &.offer-section {
                    flex: 1 1 60%;
                    border-right: 1px solid colors.$lightGrey;
                    display: flex;
                    flex-direction: column;
                    position: relative;

                    > header {
                        background-color: colors.$lightGreyBackground;
                        border-bottom: 1px solid colors.$greyBorder;
                        padding: 0.5em 1em;
                        flex: 0 0 auto;
                        position: relative;
                        height: 62px;

                        // the service list is absolutely positioned across the whole header, so when the
                        // discount button is there the list has to stop before it - otherwise the
                        // services scroll underneath it. keep in sync with .discount-button-container:
                        // padding + button + padding, the button being 0.75em padding + "%" + 0.75em at 16px
                        &.has-discount-button > .scrollable-horizontal {
                            right: 13px + 36px + 13px;
                        }

                        .discount-button-container {
                            position: absolute;
                            right: 0;
                            top: 0;
                            bottom: 0;
                            padding: 13px;
                        }
                    }

                    > .offer-view {
                        display: flex;
                        flex: 1 1 auto;

                        .item-categories {
                            flex: 0 0 auto;
                            background-color: colors.$lightGreyBackground;
                            border-right: 1px solid colors.$greyBorder;
                            position: relative;

                            min-width: 85px;
                            width: 25%;
                            max-width: 170px;
                        }

                        .offer-items-section {
                            flex: 1 1 auto;

                            .items-container {
                                flex: 1 1 auto;
                                display: flex;
                                flex-wrap: wrap;
                                align-content: flex-start;
                                justify-content: space-between;
                                padding: 3%;

                                .offer-item-tile {
                                    flex: 0 0 48%;
                                    margin-bottom: 3%;
                                    max-width: 48%;
                                    min-width: 85px;
                                }
                            }

                            &.is-preorder-offer {
                                margin-top: -36px;
                            }
                        }

                        .no-active-offer-msg,
                        .offer-loader-container {
                            flex: 1 1 auto;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                            background: #f5f5f5;

                            span {
                                color: colors.$fontColorHover;
                                font-size: 20px;
                            }
                        }
                    }

                    > .preorder-time-view {
                        flex: 1 1 auto;
                        display: flex;
                    }

                    > footer {
                        flex: 0 0 auto;
                    }

                    .add-to-cart-popover-container {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        padding-top: 62px;

                        display: flex;
                        align-items: flex-end;

                        > .blank-column {
                            flex: 0 0 auto;
                            min-width: 85px;
                            width: 25%;
                            max-width: 170px;
                        }

                        > .add-to-cart-popover {
                            flex: 1 1 auto;
                        }
                    }
                }
            }

            > .loader-container,
            > .loading-error-container {
                flex: 1 1 auto;
                display: flex;
                justify-content: center;
                background: #f5f5f5;
                border-left: 1px solid #ccc;
                border-right: 1px solid #ccc;
                padding-top: 20%;

                > div {
                    display: inline-block;
                    color: colors.$fontColorHover;
                    font-size: 20px;

                    span,
                    div {
                        display: inline-block;
                        vertical-align: middle;
                    }
                }
            }
        }

        > footer {
            flex: 0 0 auto;
            font-size: 16px;
            background-color: colors.$white;
            border-top: 1px solid #ccc;
            padding: 0.5em 1em;
            text-align: right;

            .footer-location-name {
                margin-right: 1em;
                vertical-align: text-top;
            }

            .go-to-room-plan-btn {
                font-size: 18px;
                color: colors.$white;
                display: inline-block;
                cursor: pointer;
                width: 38px;
                height: 34px;
                background-color: colors.$green;
                line-height: 34px;
                text-align: center;
                border-radius: 3px;
                transition: 0.15s linear;

                &:hover,
                &:active {
                    border-color: colors.$lightGreyBorderHover;
                    background-color: colors.$greenHover;
                    transform: rotate(90);
                }
            }
        }

        .back-to-preoder-time-btn-container {
            > div {
                background-color: #f5f5f5;
                border-right: 1px solid #dcdcdc;
                border-bottom: 1px solid #ccc;
                min-width: 85px;
                width: 25%;
                max-width: 170px;
                padding: 0.5em 1em;
                cursor: pointer;
                transition: 0.15s linear;

                @include mixins.hover-and-active() {
                    background-color: #ddd;
                }
            }
        }
    }

    .order-actions-footer {
        background-color: colors.$white;
        -webkit-box-shadow: 0px 0px 5px 0px rgba(colors.$black, 0.25);
        -moz-box-shadow: 0px 0px 5px 0px rgba(colors.$black, 0.25);
        box-shadow: 0px 0px 5px 0px rgba(colors.$black, 0.25);
        border-top: 1px solid #dcdcdc;
        padding: 0 15px 15px;

        .action-buttons-container {
            display: flex;
            align-items: center;
        }
    }

    .preorder-actions-footer {
        padding-top: 38px;
    }

    .footer-location-name {
        font-weight: 600;
        font-size: 14px;
    }

    .scrollable-horizontal {
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        position: absolute;
        -webkit-overflow-scrolling: touch;
        overflow-x: auto;
    }

    .pay-later-order-received-order-number {
        font-weight: 600;
        font-size: 42px;
        text-transform: uppercase;
        padding: 0.4em 0 0.3em;
        text-align: center;
    }

    .tableNotActivePopupContainer {
        z-index: 9999 !important;
    }
</style>
